import {
  createRouter,
  createWebHistory,
  type NavigationGuardReturn,
  type RouteRecordRaw,
} from 'vue-router'
import AuthEngine from '@/plugins/msal'

const HomePage = () => import('./views/HomePage.vue')
const ReportingWorkspace = () => import('./views/ReportingWorkspace.vue')
const ShopRevenueInput = () => import('./views/ShopRevenueInput.vue')
const AuthCallback = () => import('./components/AuthCallback.vue')
const ContactUs = () => import('./views/ContactUs.vue')
const RevenueResponseModal = () => import('./components/revenueResponse/RevenueResponseModal.vue')
const TermsAndConditions = () => import('./views/TermsAndConditions.vue')

const ShopAdministration = () => import('./views/Admin/ShopAdministration.vue')
const ShopAdministrationEdit = () => import('./views/Admin/ShopAdministrationEdit.vue')
const MemberAdministration = () => import('./views/Admin/MemberAdministration.vue')
const MemberAddUpdate = () => import('./views/Admin/MemberAddUpdate.vue')
const NotificationGroupAdministration = () =>
  import('./views/Admin/NotificationGroupAdministration.vue')
const NotificationGroupsUpdate = () => import('./views/Admin/NotificationGroupsUpdate.vue')
const ChainAdministration = () => import('./views/Admin/ChainAdministration.vue')
const ChainAdministrationEdit = () => import('./views/Admin/ChainAdministrationEdit.vue')
const SectionAdministration = () => import('./views/Admin/SectionAdministration.vue')
const SectionsAdministrationEdit = () => import('./views/Admin/SectionsAdministrationEdit.vue')

type AdminEditRouteLike = {
  params: {
    id?: unknown
  }
  query: {
    returnTo?: unknown
  }
}

type AdminEditProps = {
  id: number
  returnTo?: string
}

function mapAdminEditProps(route: AdminEditRouteLike): AdminEditProps {
  return {
    id: parseInt(String(route.params.id), 10),
    returnTo: route.query.returnTo as string | undefined,
  }
}

function memberAdminEditProps(route: AdminEditRouteLike): AdminEditProps {
  return mapAdminEditProps(route)
}

function groupAdminEditProps(route: AdminEditRouteLike): AdminEditProps {
  return mapAdminEditProps(route)
}

function shopAdminEditProps(route: AdminEditRouteLike): AdminEditProps {
  return mapAdminEditProps(route)
}

function chainAdminEditProps(route: any): { id: number; returnTo: string } {
  return { id: parseInt(route.params.id), returnTo: route.query.returnTo as string }
}

function sectionAdminEditProps(route: any): { id: number; returnTo: string } {
  return { id: parseInt(route.params.id), returnTo: route.query.returnTo as string }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'homepage',
    component: HomePage,
  },
  {
    path: '/login',
    name: 'login',
    component: HomePage,
  },
  {
    path: '/reporting',
    name: 'reportingWorkspace',
    component: ReportingWorkspace,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/shops',
    name: 'shopRevenue',
    component: ShopRevenueInput,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/contactus',
    component: ContactUs,
  },
  {
    path: '/callback',
    name: 'callback',
    component: AuthCallback,
  },
  {
    path: '/shops/submitSummary',
    name: 'submitSummary',
    component: RevenueResponseModal,
    props: true,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/shops/admin',
    name: 'shopAdmin',
    component: ShopAdministration,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/shops/admin/edit/:id',
    name: 'shopAdminEdit',
    component: ShopAdministrationEdit,
    props: shopAdminEditProps,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/shops/admin/create',
    name: 'shopAdminCreate',
    component: ShopAdministrationEdit,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/members/admin',
    name: 'memberAdmin',
    component: MemberAdministration,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/members/admin/edit/:id',
    name: 'memberAdminEdit',
    component: MemberAddUpdate,
    props: memberAdminEditProps,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/members/admin/create',
    name: 'memberAdminCreate',
    component: MemberAddUpdate,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/notificationGroup/admin',
    name: 'notificationGroupAdmin',
    component: NotificationGroupAdministration,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/notificationGroup/admin/edit/:id',
    name: 'notificationGroupAdminEdit',
    component: NotificationGroupsUpdate,
    props: groupAdminEditProps,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/tandc',
    name: 'termsAndConditions',
    component: TermsAndConditions,
  },
  {
    path: '/chains/admin',
    name: 'ChainAdministration',
    component: ChainAdministration,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/chains/admin/edit/:id',
    name: 'ChainAdministrationEdit',
    component: ChainAdministrationEdit,
    props: chainAdminEditProps,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/chains/admin/create',
    name: 'ChainAdministrationCreate',
    component: ChainAdministrationEdit,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/sections/admin',
    name: 'SectionsAdministration',
    component: SectionAdministration,
    meta: {
      authorize: true,
    },
  },
  {
    path: '/sections/admin/edit/:id',
    name: 'SectionsAdministrationEdit',
    component: SectionsAdministrationEdit,
    props: sectionAdminEditProps,
    meta: {
      authorize: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

type GuardRoute = {
  fullPath: string
  matched: Array<{
    meta?: {
      authorize?: boolean
    }
  }>
}

export function authorizeRouteGuard(
  to: GuardRoute,
  _from: GuardRoute,
): NavigationGuardReturn {
  if (to.matched.some((record) => record.meta?.authorize) && !AuthEngine.isAuthenticated()) {
    return {
      path: '/login',
      query: { target: to.fullPath },
    }
  }

  return true
}

router.beforeEach(authorizeRouteGuard)

export default router
